import { DisplayType, DisplayValue, LogManager, Navigation } from "@mcleod/core";
import {
    Button, ClickEvent, Component, ComponentSearcher, Label, Layout, Tab, Table, TableRow, TableRowDisplayEvent, Textbox
} from "@mcleod/components";
import { OrderTabView } from "@mcleod/dispatch";
import { Customer } from "@mcleod/ar";
import { BrokerageMovementTable } from "./BrokeragePlanningUtil";
import { BrokerageMovementTableType } from "./BrokerageMovementTableType";
import { BrokeragePlanning } from "./BrokeragePlanning";
import {
    BrokeragePlanningUtil, MovementStatus, rowMinuteUpdate, setTooltipContent, setupBrkStatusPanel,
    tableSearcherCreationCallback
} from "./BrokeragePlanningUtil";
import { AutogenLayoutBrokeragePlanningPeriscope } from "./autogen/AutogenLayoutBrokeragePlanningPeriscope";
import { BrokeragePlanningToolPanel } from "./BrokeragePlanningToolPanel";
import { PlanningUtil } from "@mcleod/dispatch/src/planning/PlanningUtil";
import {  TableSelectionEvent } from "@mcleod/components";

const log = LogManager.getLogger("lme.powerbroker.BrokeragePlanningPeriscope");

export class BrokeragePlanningPeriscope extends AutogenLayoutBrokeragePlanningPeriscope {
    private _type: BrokerageMovementTableType;
    private _brokeragePlanning: BrokeragePlanning;
    private toolPanelLayout: BrokeragePlanningToolPanel;

    async onLoad(): Promise<void> {
        this.toolPanelLayout = Layout.getLayout("lme/powerbroker/BrokeragePlanningToolPanel") as BrokeragePlanningToolPanel;
        await this.toolPanelLayout.ensureLoaded();
        this.toolPanelLayout.table = this.tableMovements;
        this.toolPanelLayout.initialize();
    }

    configureTableForType(type: BrokerageMovementTableType) {
        this._type = type;
        this.tableMovements.dataSource.orderBy = this._type.defaultSort;
    }

    public set brokeragePlanning(planning: BrokeragePlanning) {
        this._brokeragePlanning = planning;
    }

    public get type(): BrokerageMovementTableType {
        return this._type;
    }

    public get filter() {
        return this.type.filter;
    }

    public async updateFilterCounts() {
        return;
    }

    tableMovementsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const orderIdLabel = tableRow.findComponentById("labelOrderId") as Label;
        if (orderIdLabel != null) {
            orderIdLabel.addUnderlineWhenMouseOver();
            orderIdLabel.visible = modelRow.isNull("manifest_id");
        }
        const customerLabel = tableRow.findComponentById("labelCustomer") as Label;
        if (customerLabel != null) {
            customerLabel.addUnderlineWhenMouseOver();
        }
        const imageOnHold = tableRow.findComponentById("imageOnHold") as Button;
        if (imageOnHold != null) {
            imageOnHold.visible = modelRow.getBoolean("hold", false) && modelRow.getString("manifest_id") == null;
            imageOnHold.tooltip = modelRow.getString("hold_reason");
        }
        const imageManifestOnHold = tableRow.findComponentById("imageManifestOnHold") as Button;
        if (imageManifestOnHold != null) {
            imageManifestOnHold.visible = modelRow.getBoolean("manifest_hold", false);
            imageManifestOnHold.tooltip = "Manifest On Hold";
        }
        tableRow.addDblClickListener(event => {
            const movementStatus = modelRow.get("move_status");
            const movementId = modelRow.get("movement_id");
            let selectedTab: Tab;
            log.debug(movementStatus)
            switch (movementStatus) {
                case MovementStatus.AVAILABLE:
                    selectedTab = this._brokeragePlanning.tabAvailableOrders;
                    break;
                case MovementStatus.COVERED:
                    selectedTab = this._brokeragePlanning.tabCoveredOrders;
                    break;
                case MovementStatus.PROGRESS:
                    selectedTab = this._brokeragePlanning.tabInProgressOrders;
                    break;
                case MovementStatus.DELIVERED:
                    selectedTab = this._brokeragePlanning.tabDeliveredOrders;
                    break;
                default:
            }
            if (selectedTab) {
                selectedTab.select();
                const layout = selectedTab.components[0] as BrokerageMovementTable;
                const selectedRow = layout.tableMovements.rows.find((row: TableRow) => row.data?.get("movement_id") === movementId);
                if (selectedRow != null) {
                    this.populateTableInDOM(layout.tableMovements, movementId).then(() => {
                        layout.tableMovements.selectedRow = selectedRow;
                        layout.tableMovements.scrollToSelection();
                    });
                }
            }
        });
        tableRow.contextMenuItems = () => BrokeragePlanningUtil.getContextMenuItems(this.toolPanelLayout, tableRow)
        setupBrkStatusPanel(tableRow);
        this.tableMovements.addlSearcherCallback = (): ComponentSearcher[] => tableSearcherCreationCallback();
        rowMinuteUpdate(tableRow, this.type);
        tableRow.forEveryChildComponent(comp => {
            setTooltipContent(comp, modelRow);
        });
        const manifestIdLabel = tableRow.findComponentById("labelManifestId") as Label;
        if (manifestIdLabel != null)
            manifestIdLabel.addUnderlineWhenMouseOver();

        const labelCustomerName = tableRow.findComponentById("labelCustomerName");
        if (labelCustomerName && !modelRow.isNull("manifest_id")) {
            labelCustomerName.visible = false;
        } else {
            const textboxManifestCustomers = tableRow.findComponentById("textboxManifestCustomers");
            if (textboxManifestCustomers) {
                textboxManifestCustomers.visible = false;
            }
        }
        const labelOriginEarly = tableRow.findComponentById("labelOriginEarly") as Label;
        if (labelOriginEarly && modelRow.getDateTime("origin_late") != null) {
            if (labelOriginEarly) {
                labelOriginEarly.text = DisplayValue.getDisplayValue(modelRow.getDateTime("origin_late"), DisplayType.DATETIME);
            }
        }
        const labelDestinationEarly = tableRow.findComponentById("labelDestEarly") as Label;
        if (labelDestinationEarly && modelRow.getDateTime("destination_late") != null) {
            labelDestinationEarly.text = DisplayValue.getDisplayValue(modelRow.getDateTime("destination_late"), DisplayType.DATETIME);
        }
        const textboxLastCallinEta = tableRow.findComponentById("textboxLastCallinEta") as Textbox;
        if (textboxLastCallinEta != null && textboxLastCallinEta.text != null && modelRow.get("next_stop_timezone") != null) {
            textboxLastCallinEta.text = DisplayValue.getDisplayValue(modelRow.getDateTime("last_callin_eta"), DisplayType.DATETIME) + " " + modelRow.get("next_stop_timezone");
        }
        PlanningUtil.checkScheduledLate(tableRow, "origin");
        PlanningUtil.checkScheduledLate(tableRow, "destination");
        PlanningUtil.checkAppointmentRequiredConfirmed(tableRow);
        PlanningUtil.displayDetentionIcon(tableRow);
    }


    async populateTableInDOM(table: Table, movementId: string) {
        let i = 0;
        let currRow = table.rows[i];
        const tasks = [];
        while (currRow != null && currRow.data.get("movement_id") !== movementId) {
            tasks.push(currRow.populateDOMIfNeeded());
            i++;
            currRow = table.rows[i];
        }
        await Promise.all(tasks);
    }

    orderNumberOnClick(event: ClickEvent) {
        OrderTabView.navigateTo({ key: (event.target as Label).text, newTab: true });
    }

    customerIdOnClick(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        Customer.navigateTo(row.data.get("customer_id"));
    }

    manifestIdOnClick(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + row.data.get("manifest_id"), { newTab: true });
    }

    /** This is an event handler for the onSelect event of tableMovements. */
    tableMovementsOnSelect(event: TableSelectionEvent) {
        this.toolPanelLayout.tableMovementsOnSelect(event);
    }
}
